/* eslint-disable max-lines, max-lines-per-function */
import React from "react";
import {
  Button,
  Typography,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
  Grid,
  Link as MuiLink,
  Stack,
} from "@mui/material";
import { ApolloError, gql } from "@apollo/client";
import { cleanApolloErrorMessage } from "utils/apollo";
import { Modal } from "components/haven-design/modal";

export type ContractRow = {
  id: string;
  interestListId: string | null;
  propertyName: string | null;
  propertyLegalName: string | null;
  signerName: string | null;
  signerEmail: string | null;
  signerTitle: string | null;
  signatureRequestId: string | null;
  submissionId: string | null;
  embedded: boolean | null;
  status: string | null;
  sentAt: string | null;
  signedAt: string | null;
  cancelledAt: string | null;
  contractStartDate: unknown | null;
  initialTermEndDate: unknown | null;
  pdfUrl: string | null;
}

export type PropertyContractsData = {
  propertyContracts: ContractRow[];
}

export const MANAGER_CONTRACTS_ROUTE = "/dash/manager/settings/contracts";

export const managerContractDetailHref = (id: string, fromList = false): string =>
  (fromList ? `${MANAGER_CONTRACTS_ROUTE}/${id}?from=list` : `${MANAGER_CONTRACTS_ROUTE}/${id}`);

type ContractStatusColor = "default" | "warning" | "success" | "info" | "error";

export const PROPERTY_CONTRACTS_QUERY = gql`
  query PropertyContractsShared {
    propertyContracts {
      id
      interestListId
      propertyName
      propertyLegalName
      signerName
      signerEmail
      signerTitle
      signatureRequestId
      submissionId
      embedded
      status
      sentAt
      signedAt
      cancelledAt
      contractStartDate
      initialTermEndDate
      pdfUrl
    }
  }
`;

export const RESEND_MUTATION = gql`
  mutation ResendPropertyContractShared(
    $id: ID!,
    $signerName: String,
    $signerEmail: String
  ) {
    resendPropertyContract(
      id: $id,
      signerName: $signerName,
      signerEmail: $signerEmail
    ) {
      signatureRequestId
      signUrl
    }
  }
`;

export const SIGN_URL_MUTATION = gql`
  mutation PropertyContractSignUrlShared($id: ID!, $timeZone: String) {
    propertyContractSignUrl(id: $id, timeZone: $timeZone)
  }
`;

export const MARK_PENDING_MUTATION = gql`mutation MarkPropertyContractPendingShared($id: ID!) {
  markPropertyContractPending(id: $id)
}`;

export const ARCHIVE_MUTATION = gql`mutation ArchivePropertyContractShared($id: ID!) {
  archivePropertyContract(id: $id)
}`;

export const statusChipColor = (status: string | null | undefined): ContractStatusColor => {
  switch ((status || "").toLowerCase()) {
    case "draft":
      return "warning";
    case "signed":
      return "success";
    case "sent":
    case "pending":
      return "info";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

export const statusChipLabel = (status: string | null | undefined): string => {
  if (!status) return "Unknown";
  if (status === "sent") return "Pending";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const detailValue = (value: string | null | undefined): string => (value && value.trim() !== "" ? value : "-");
const HELLOSIGN_REMINDER_RATE_LIMIT_RE = /can only send one reminder to .+ each hour\.?/i;

const HELLOSIGN_ALREADY_SIGNED_RE =
  /(?:already signed|has been signed|signature request.+complete|cannot remind.+complete)/i;

export const REMINDER_ALREADY_SIGNED_MESSAGE = "This document has already been signed";

const mapKnownReminderResendMessages = (message: string): string => {
  const t = message.trim();
  if (HELLOSIGN_REMINDER_RATE_LIMIT_RE.test(t)) return "One reminder per hour max";
  if (HELLOSIGN_ALREADY_SIGNED_RE.test(t)) return REMINDER_ALREADY_SIGNED_MESSAGE;
  return message;
};

export const reminderResendUserMessage = (err: unknown): string => {
  let base = "Reminder failed";
  if (err instanceof ApolloError) {
    const gqlMsg = err.graphQLErrors?.[0]?.message;
    if (gqlMsg) base = cleanApolloErrorMessage(gqlMsg);
    else if (err.message) base = cleanApolloErrorMessage(err.message);
  } else if (err instanceof Error && err.message) {
    base = cleanApolloErrorMessage(err.message);
  }
  return mapKnownReminderResendMessages(base);
};
type ContractDetailsCardProps = {
  selectedContract: ContractRow;
  busyId?: string | null;
  signedContractId?: string | null;
  reminderSentForContractId?: string | null;
  reminderFailure?: { contractId: string; message: string } | null;
  archiving: boolean;
  onResend?: (contract: ContractRow) => void;
  onSignNow?: (contract: ContractRow) => void;
  onDeleteDraft: () => void;
  enableReminder?: boolean;
}

export const ContractDetailsCard: React.FC<ContractDetailsCardProps> = ({
  selectedContract,
  busyId,
  signedContractId,
  reminderSentForContractId,
  reminderFailure,
  archiving,
  onResend,
  onSignNow,
  onDeleteDraft,
  enableReminder = true,
}) => {
  // Once the HelloSign modal reports a successful sign, treat the contract as pending
  // locally (before the server status refetch lands) so Sign Now / Delete Draft lock
  // immediately: it is no longer a sendable/deletable contract.
  const justSigned = signedContractId === selectedContract.id;
  const status = justSigned ? "pending" : selectedContract.status ?? "";
  const canSignNow = status === "sent" && Boolean(onSignNow);
  const canSendReminder = enableReminder && !canSignNow && status === "sent";
  const canDelete = ["draft", "sent"].includes(status);
  const showAutoReminderNote = status === "sent";

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="caption" color="textSecondary" gutterBottom component="div">
            Property Legal Name
          </Typography>
          <Typography variant="body2">
            {selectedContract.propertyLegalName ?? selectedContract.propertyName ?? "N/A"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant="caption" color="textSecondary" gutterBottom component="div">
            Contract status
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={statusChipLabel(selectedContract.status)}
            color={statusChipColor(selectedContract.status)}
          />
        </Grid>
        <Grid size={12}>
          <Typography variant="caption" color="textSecondary" gutterBottom component="div">
            Signer
          </Typography>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size="auto">
              <Typography variant="body2" component="span">
                {detailValue(selectedContract.signerName)}
                {" - "}
                {detailValue(selectedContract.signerEmail)}
              </Typography>
            </Grid>
            {(canSendReminder || canSignNow) && (
              <Grid size="auto">
                <Stack spacing={0.5} alignItems="flex-start">
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      if (canSignNow) onSignNow?.(selectedContract);
                      else onResend?.(selectedContract);
                    }}
                    disabled={
                      busyId === selectedContract.id ||
                      reminderSentForContractId === selectedContract.id
                    }
                  >
                    {canSignNow ? "Sign Now" : "Send reminder"}
                  </Button>
                  {!canSignNow && reminderSentForContractId === selectedContract.id && (
                    <Typography variant="caption" color="success.main" component="p" sx={{ m: 0 }}>
                      Reminder sent
                    </Typography>
                  )}
                  {!canSignNow && reminderFailure?.contractId === selectedContract.id && (
                    <Typography
                      variant="caption"
                      color="error.main"
                      component="p"
                      sx={{ m: 0, wordBreak: "break-word" }}
                    >
                      {reminderFailure.message}
                    </Typography>
                  )}
                </Stack>
              </Grid>
            )}
          </Grid>
          {showAutoReminderNote && (
            <Typography variant="caption" color="textSecondary" component="p" sx={{ mt: 1, mb: 0 }}>
              Automatic reminders sent on 3rd and 7th day
            </Typography>
          )}
        </Grid>
        {(canDelete || selectedContract.pdfUrl) && (
          <Grid size={12}>
            <Grid container spacing={1}>
              {canDelete && (
                <Grid size="auto">
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={onDeleteDraft}
                    disabled={archiving}
                  >
                    Delete
                  </Button>
                </Grid>
              )}
              {selectedContract.pdfUrl && (
                <Grid size="auto">
                  <MuiLink href={selectedContract.pdfUrl} target="_blank" rel="noopener noreferrer">
                    <Button size="small" variant="outlined">Download PDF</Button>
                  </MuiLink>
                </Grid>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

type DeletePendingModalProps = {
  isOpen: boolean;
  archiving: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DeletePendingModal: React.FC<DeletePendingModalProps> = ({
  isOpen,
  archiving,
  onClose,
  onConfirm,
}) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onClose}
    fullWidth
    maxWidth="sm"
    title="Delete pending"
  >
    <DialogContent>
      <Typography variant="body2">
        No one will be able to sign this document and all copies will be deleted.
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button color="secondary" variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button color="secondary" variant="contained" onClick={onConfirm} disabled={archiving}>
        Delete
      </Button>
    </DialogActions>
  </Modal>
);
